'use client';

/**
 * Institutional / academic learning-portal card. 16:10 cover with
 * level chip top-left and star+rating top-right, two-line title,
 * instructor row with avatar dot, duration + reviews footer.
 *
 * <p>Originally written as {@code BtkCourseCard}; renamed and made
 * generic. Tenants wanting localised labels register a theme primitive
 * via the {@code 'custom'} variant slot.
 */
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function escapeUrl(s: string): string {
  return s.replace(/'/g, "\\'").replace(/"/g, '\\"');
}

function StarIcon({ size = 12, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      style={{ display: 'inline-block' }}
      aria-hidden
    >
      <path d="M8 1.5l1.99 4.03 4.45.65-3.22 3.14.76 4.43L8 11.65l-3.98 2.1.76-4.43L1.56 6.18l4.45-.65L8 1.5z" />
    </svg>
  );
}

export function AcademicCourseCard({
  course: c,
  showRating = true,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  showRating?: boolean;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const ratingNum = clamp(c.ratingAverage ?? 0, 0, 5);
  const ratingLabel = showRating && ratingNum > 0 ? ratingNum.toFixed(1) : null;
  const durationLabel = c.totalHours ? `${c.totalHours}h` : c.duration;

  return (
    <Card asChild>
      <PortalLink
        href={c.href}
        style={{
          ...cardReset,
          display: 'block',
          textDecoration: 'none',
          color: 'var(--color-text-primary, #ffffff)',
          background: 'var(--color-surface-card, #141414)',
          borderRadius: 'var(--radius-md, 8px)',
          overflow: 'hidden',
          border: '1px solid var(--color-border-subtle, rgba(255,255,255,0.06))',
          transition: 'transform 180ms ease, background 180ms ease',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            aspectRatio: 'var(--app-course-image-aspect-ratio, 16 / 10)',
            background: 'var(--color-surface-elevated, #1f1f1f)',
          }}
        >
          <CourseCoverImage
            image={typeof c.image === 'string' ? c.image : undefined}
            resizeTo="480x0"
            sizes="(max-width: 640px) 100vw, 340px"
            gradient="linear-gradient(135deg, var(--color-surface-elevated, #2a2a2a) 0%, var(--color-surface-card, #1a1a1a) 100%)"
          />
          <LevelBadge level={c.level} style={{ position: 'absolute', top: 10, left: 10 }} />
          {ratingLabel && (
            <span
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                padding: 'var(--space-1, 4px) var(--space-2, 8px)',
                fontSize: '11px',
                fontWeight: 500,
                color: 'var(--color-text-inverse, #0a0a0a)',
                background: 'var(--color-brand-500, rgba(255,255,255,0.92))',
                borderRadius: 'var(--radius-full, 999px)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <StarIcon size={11} filled />
              {ratingLabel}
            </span>
          )}
        </div>

        <div
          style={{ padding: 'var(--space-3-5, 14px) var(--space-3-5, 14px) var(--space-4, 16px)' }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 500,
              margin: 0,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {c.title}
          </h3>

          {c.instructor && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 12,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: c.instructorAvatar
                    ? `var(--color-surface-elevated, #2a2a2a) url(${escapeUrl(c.instructorAvatar)}) center/cover no-repeat`
                    : 'var(--color-surface-elevated, #2a2a2a)',
                  flexShrink: 0,
                }}
                aria-hidden
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--color-text-secondary, rgba(255,255,255,0.78))',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {c.instructor}
                </div>
              </div>
            </div>
          )}

          {showProgress && typeof c.progress === 'number' && c.progress > 0 && (
            <div style={{ marginTop: 10 }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}

          {(durationLabel || (showRating && c.ratingCount)) && (
            <div
              style={{
                marginTop: 10,
                fontSize: '11px',
                color: 'var(--color-text-tertiary, rgba(255,255,255,0.45))',
                display: 'flex',
                gap: 10,
              }}
            >
              {durationLabel && <span>{durationLabel}</span>}
              {showRating && c.ratingCount && (
                <span>· {c.ratingCount.toLocaleString()} reviews</span>
              )}
            </div>
          )}
        </div>
      </PortalLink>
    </Card>
  );
}

AcademicCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 260,
  gap: 'clamp(14px, 2vw, 18px)',
} satisfies CardGridSpec;
